import { useEffect, useState, useCallback } from "react";
import { apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { useVelvet } from "@/context/VelvetContext";
import { fmtDate } from "@/lib/calc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  RefreshCw, Globe, Package, ShoppingBag, CheckCircle2, CircleAlert,
  FlaskConical, Link2, Zap, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const MATCH_LABEL = {
  matched: { label: 'Vinculado', cls: 'bg-[hsl(140_40%_88%)] border-[hsl(140_30%_60%)]' },
  pending: { label: 'Sin vincular', cls: 'bg-[hsl(48_100%_92%)] border-[hsl(38_60%_60%)]' },
  ambiguous: { label: 'Ambiguo · revisar', cls: 'bg-[hsl(0_60%_92%)] border-[hsl(0_50%_60%)]' },
  ignored: { label: 'Ignorado', cls: 'bg-muted border-border' },
};

const PAY_COLOR = {
  PAID: 'text-[hsl(140_50%_35%)]',
  PENDING: 'text-[hsl(38_60%_45%)]',
  AUTHORIZED: 'text-[hsl(220_50%_45%)]',
  FAILED: 'text-destructive',
  REFUNDED: 'text-muted-foreground',
  NOT_CHARGED: 'text-muted-foreground',
  PARTIALLY_PAID: 'text-[hsl(38_60%_45%)]',
  REFUND_PENDING: 'text-[hsl(38_60%_45%)]',
  REFUND_FAILED: 'text-destructive',
};

export default function Squarespace() {
  const { profile } = useAuth();
  const { products, customers } = useVelvet();
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState('products');
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);   // {kind, id}
  const [convertTarget, setConvertTarget] = useState(null); // {row, preview}
  const canTrigger = ['admin', 'manager'].includes(profile?.role);
  const isAdmin = profile?.role === 'admin';

  const loadStatus = useCallback(async () => {
    try { setStatus(await apiFetch('/squarespace/status')); } catch (e) { /* silent */ }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const path = tab === 'products' ? '/squarespace/products' : '/squarespace/orders';
      const q = filter === 'all' ? '' : `?match=${filter}`;
      const data = await apiFetch(path + q);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab, filter]);

  useEffect(() => { loadStatus(); loadRows(); }, [loadStatus, loadRows]);

  const runCatalog = async () => {
    setSyncing(true);
    try {
      const r = await apiFetch('/squarespace/sync/catalog', { method: 'POST' });
      toast.success(`Catálogo: ${r.products} productos (${r.matched} vinculados, ${r.pending} pendientes, ${r.ambiguous} ambiguos)`);
      await loadStatus(); await loadRows();
    } catch (e) { toast.error(e.message); }
    finally { setSyncing(false); }
  };

  const runOrders = async () => {
    setSyncing(true);
    try {
      // Squarespace order pagination can legitimately take up to ~85s for a large
      // window; allow the client 100s so the backend's own 80s budget wins first.
      const r = await apiFetch('/squarespace/sync/orders', {
        method: 'POST', body: JSON.stringify({}), timeoutMs: 100000,
      });
      const from = new Date(r.window_from).toLocaleString();
      if (r.partial) {
        toast.warning(`Sincronización parcial: ${r.orders} pedidos. El checkpoint no avanzó; ejecuta de nuevo para completar.`);
      } else {
        toast.success(`Pedidos: ${r.orders} sincronizados desde ${from}`);
      }
      await loadStatus(); await loadRows();
    } catch (e) { toast.error(e.message); }
    finally { setSyncing(false); }
  };

  const test = async () => {
    setSyncing(true);
    try {
      const r = await apiFetch('/squarespace/test-connection', { method: 'POST' });
      if (r.ok) toast.success('Conexión Squarespace OK');
      else toast.error(`Prueba falló: ${r.error}`);
      await loadStatus();
    } catch (e) { toast.error(e.message); }
    finally { setSyncing(false); }
  };

  const simulate = async () => {
    setSyncing(true);
    try {
      const r = await apiFetch('/squarespace/simulate', { method: 'POST' });
      toast.success(`Simulación: ${r.simulated_products} productos y ${r.simulated_orders} pedidos`);
      await loadStatus(); await loadRows();
    } catch (e) { toast.error(e.message); }
    finally { setSyncing(false); }
  };

  const linkProduct = async ({ product_id, unlink }) => {
    try {
      await apiFetch(`/squarespace/products/${linkTarget.id}/link`, {
        method: 'POST', body: JSON.stringify({ product_id, unlink }),
      });
      toast.success(unlink ? 'Producto desvinculado' : 'Producto vinculado');
      setLinkTarget(null); await loadRows(); await loadStatus();
    } catch (e) { toast.error(e.message); }
  };

  const linkCustomer = async ({ customer_id, unlink }) => {
    try {
      await apiFetch(`/squarespace/orders/${linkTarget.id}/link-customer`, {
        method: 'POST', body: JSON.stringify({ customer_id, unlink }),
      });
      toast.success(unlink ? 'Cliente desvinculado' : 'Cliente vinculado');
      setLinkTarget(null); await loadRows(); await loadStatus();
    } catch (e) { toast.error(e.message); }
  };

  const openConvert = async (row, openExisting) => {
    if (openExisting && row.velvet_order_id) {
      // Navigate to Orders page with a highlight anchor. Orders page reads ?highlight= param.
      window.location.href = `/orders?highlight=${encodeURIComponent(row.velvet_order_id)}`;
      return;
    }
    try {
      const preview = await apiFetch(`/squarespace/orders/${row.id}/convert-preview`);
      setConvertTarget({ row, preview });
    } catch (e) { toast.error(e.message); }
  };

  const doConvert = async () => {
    if (!convertTarget) return;
    try {
      const r = await apiFetch(`/squarespace/orders/${convertTarget.row.id}/convert`, {
        method: 'POST', body: JSON.stringify({ confirm: true }),
      });
      if (r.already_converted) {
        toast.info(`Ya convertido como ${r.order_number}`);
      } else {
        toast.success(`Pedido Velvet ${r.order_number} creado (pago Squarespace: ${r.payment_state_preserved || 'n/a'})`);
      }
      setConvertTarget(null);
      await loadRows();
    } catch (e) {
      const blockers = e.data?.detail?.blockers;
      if (Array.isArray(blockers)) {
        toast.error('Conversión bloqueada: ' + blockers.join(' · '));
      } else {
        toast.error(e.message);
      }
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-10 max-w-[1500px]" data-testid="squarespace-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="velvet-eyebrow mb-3">Integrations</div>
          <h1 className="velvet-page-title flex items-center gap-3">
            Squarespace <span className="text-muted-foreground text-lg">·</span>
            <span className="font-serif text-xl text-[hsl(var(--muted-green-dark))]">Sync</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-3">Catálogo web y pedidos importados en solo lectura desde tu tienda Squarespace.</p>
        </div>
        <StatusChip status={status} />
      </div>

      {/* Status + controls card */}
      <div className="velvet-card p-6 mb-6" data-testid="sq-status-card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <MetricBox label="Productos" value={status?.counts?.products || 0}
                     hint={`${status?.counts?.products_matched || 0} vinculados · ${status?.counts?.products_pending || 0} pendientes`} />
          <MetricBox label="Pedidos" value={status?.counts?.orders || 0}
                     hint={`${status?.counts?.orders_customer_matched || 0} cliente vinculado`} />
          <MetricBox label="Último catálogo" value={status?.settings?.last_catalog_sync_at ? fmtDate(status.settings.last_catalog_sync_at) : '—'} />
          <MetricBox label="Últimos pedidos" value={status?.settings?.last_orders_sync_at ? fmtDate(status.settings.last_orders_sync_at) : '—'} />
        </div>
        {canTrigger && (
          <div className="flex flex-wrap gap-2 mb-4">
            <Button onClick={runCatalog} disabled={syncing}
                    className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                    data-testid="sync-catalog-btn">
              {syncing ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Package className="h-4 w-4 mr-1" />}
              Sincronizar catálogo
            </Button>
            <Button onClick={runOrders} disabled={syncing} variant="outline" className="rounded-sm"
                    data-testid="sync-orders-btn">
              {syncing ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <ShoppingBag className="h-4 w-4 mr-1" />}
              Sincronizar pedidos (incremental)
            </Button>
            <Button onClick={test} disabled={syncing} variant="outline" className="rounded-sm"
                    data-testid="test-connection-btn">
              <Zap className="h-4 w-4 mr-1" /> Probar conexión
            </Button>
            {isAdmin && (
              <Button onClick={simulate} disabled={syncing} variant="ghost"
                      className="rounded-sm text-muted-foreground hover:text-foreground"
                      data-testid="simulate-btn">
                <FlaskConical className="h-4 w-4 mr-1" /> Cargar datos simulados
              </Button>
            )}
          </div>
        )}
        {/* Recent runs / errors */}
        <RecentRuns runs={status?.recent_runs || []} />
      </div>

      {/* Data tabs */}
      <div className="flex items-center gap-2 mb-4" data-testid="sq-tabs">
        <TabBtn active={tab === 'products'} onClick={() => setTab('products')} testid="tab-products">
          <Package className="h-3.5 w-3.5" /> Productos web
        </TabBtn>
        <TabBtn active={tab === 'orders'} onClick={() => setTab('orders')} testid="tab-orders">
          <ShoppingBag className="h-3.5 w-3.5" /> Pedidos web
        </TabBtn>
        <div className="ml-auto flex items-center gap-2">
          <span className="velvet-eyebrow text-muted-foreground">Filtro</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="rounded-sm h-9 w-44" data-testid="match-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="matched">Vinculados</SelectItem>
              <SelectItem value="pending">Sin vincular</SelectItem>
              <SelectItem value="ambiguous">Ambiguos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="velvet-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Cargando…</div>
        ) : tab === 'products' ? (
          <ProductsTable rows={rows} canTrigger={canTrigger}
                         onLink={(row) => setLinkTarget({ kind: 'product', id: row.id, row })} />
        ) : (
          <OrdersTable rows={rows} canTrigger={canTrigger}
                       onLink={(row) => setLinkTarget({ kind: 'order', id: row.id, row })}
                       onConvert={openConvert} />
        )}
      </div>

      {linkTarget?.kind === 'product' && (
        <LinkDialog title="Vincular producto web"
                    description={`${linkTarget.row.name || linkTarget.row.sku || '—'}${linkTarget.row.is_simulated ? ' · SIM' : ''}`}
                    options={products.map(p => ({ id: p.id, label: `${p.name}${p.product_sku ? ` (${p.product_sku})` : ''}` }))}
                    currentId={linkTarget.row.product_id}
                    onSubmit={linkProduct}
                    onClose={() => setLinkTarget(null)} />
      )}
      {linkTarget?.kind === 'order' && (
        <LinkDialog title="Vincular cliente del pedido"
                    description={`Pedido ${linkTarget.row.order_number || linkTarget.row.external_id} — ${linkTarget.row.customer_email || 'sin email'}`}
                    options={customers.map(c => ({ id: c.id, label: `${c.name}${c.email ? ` (${c.email})` : ''}` }))}
                    currentId={linkTarget.row.customer_id}
                    onSubmit={linkCustomer}
                    onClose={() => setLinkTarget(null)}
                    valueKey="customer_id" />
      )}

      {convertTarget && (
        <ConvertDialog target={convertTarget}
                       onConfirm={doConvert}
                       onClose={() => setConvertTarget(null)} />
      )}
    </div>
  );
}

function ConvertDialog({ target, onConfirm, onClose }) {
  const { row, preview } = target;
  const blockers = preview?.blockers || [];
  const canConvert = preview?.can_convert && !preview?.already_converted;
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Convertir en pedido Velvet</DialogTitle>
          <DialogDescription>
            Squarespace {row.order_number || row.external_id} · pago:{' '}
            <span className="font-medium">{preview?.payment_state || '—'}</span>
            {preview?.grand_total != null && (
              <> · total {preview.currency || '$'} {Number(preview.grand_total).toFixed(2)}</>
            )}
          </DialogDescription>
        </DialogHeader>

        {row.is_simulated && (
          <div className="border border-[hsl(38_60%_60%)] bg-[hsl(48_100%_92%)] p-3 text-sm" data-testid="simulated-block">
            Este pedido es simulado; no puede convertirse en un pedido real.
          </div>
        )}

        {blockers.length > 0 && !row.is_simulated && (
          <div className="border border-destructive/40 bg-destructive/5 p-3" data-testid="convert-blockers">
            <div className="velvet-eyebrow text-destructive mb-2">Falta información antes de convertir</div>
            <ul className="text-sm space-y-1 list-disc pl-4">
              {blockers.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>
        )}

        {canConvert && (
          <div className="text-sm text-muted-foreground">
            Se creará un pedido Velvet en estado <span className="font-medium">Received</span> con {preview?.line_items?.length || 0} artículo(s).
            El estado de pago Squarespace se conserva en la nota interna — nunca se marca como pagado si no lo está.
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="convert-cancel-btn">
            Cancelar
          </Button>
          <Button onClick={onConfirm}
                  disabled={!canConvert}
                  className="rounded-sm bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40"
                  data-testid="convert-confirm-btn">
            Confirmar conversión
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusChip({ status }) {
  if (!status) return null;
  const configured = status.configured;
  const enabled = status.enabled;
  const cls = configured && enabled
    ? 'border-[hsl(140_30%_50%)] bg-[hsl(140_40%_92%)] text-foreground'
    : 'border-[hsl(38_60%_65%)] bg-[hsl(48_100%_92%)] text-foreground';
  const Icon = configured && enabled ? CheckCircle2 : CircleAlert;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-1 border ${cls}`}
          data-testid="sq-status-chip">
      <Icon className="h-3 w-3" />
      {configured && enabled ? 'Conectado a Squarespace' : 'Modo simulación'}
    </span>
  );
}

function MetricBox({ label, value, hint }) {
  return (
    <div className="border border-border p-3 bg-card">
      <div className="velvet-eyebrow text-muted-foreground">{label}</div>
      <div className="font-serif text-xl mt-1">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs uppercase tracking-[0.14em] border ${active ? 'bg-foreground text-background border-foreground' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`}>
      {children}
    </button>
  );
}

function RecentRuns({ runs }) {
  if (!runs.length) return <div className="text-xs text-muted-foreground italic">Aún sin sincronizaciones registradas.</div>;
  return (
    <div className="border border-border" data-testid="recent-runs">
      <div className="velvet-eyebrow px-3 py-2 border-b border-border text-muted-foreground">Últimas ejecuciones</div>
      <div className="divide-y divide-border">
        {runs.slice(0, 5).map((r) => (
          <div key={r.id} className="flex items-start gap-3 px-3 py-2 text-xs" data-testid={`run-${r.id}`}>
            <div className={`w-2 h-2 rounded-full mt-1.5 ${r.ok ? 'bg-[hsl(140_50%_45%)]' : 'bg-destructive'}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="uppercase tracking-[0.14em] text-muted-foreground">{r.kind}</span>
                {r.is_simulated && <FlaskConical className="h-3 w-3 text-[hsl(38_60%_45%)]" />}
                <span className="text-muted-foreground">· {fmtDate(r.started_at)}</span>
              </div>
              <div className="text-muted-foreground truncate">
                {r.ok
                  ? JSON.stringify(r.result || {})
                  : <span className="text-destructive"><AlertTriangle className="h-3 w-3 inline mr-1" />{r.error || 'error'}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductsTable({ rows, canTrigger, onLink }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b-2 border-foreground/15 text-left">
          <th className="px-6 py-3 velvet-eyebrow">Producto Squarespace</th>
          <th className="px-6 py-3 velvet-eyebrow">SKU</th>
          <th className="px-6 py-3 velvet-eyebrow">Precio</th>
          <th className="px-6 py-3 velvet-eyebrow">Estado</th>
          <th className="px-6 py-3 velvet-eyebrow">Vinculado a</th>
          <th className="px-6 py-3 velvet-eyebrow"></th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={6} className="px-6 py-16 text-center text-muted-foreground">Sin productos importados.</td></tr>
        )}
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-border" data-testid={`sq-product-${r.id}`}>
            <td className="px-6 py-4">
              <div className="font-medium flex items-center gap-2">
                {r.name || '—'}
                {r.is_simulated && <span className="text-[9px] uppercase tracking-[0.14em] px-1 py-0.5 border border-[hsl(38_60%_60%)] bg-[hsl(48_100%_92%)]">Sim</span>}
              </div>
              <div className="text-xs text-muted-foreground">{r.external_id}</div>
            </td>
            <td className="px-6 py-4 font-mono text-xs">{r.sku || '—'}</td>
            <td className="px-6 py-4 velvet-money">{r.price ? `${r.currency || '$'} ${Number(r.price).toFixed(2)}` : '—'}</td>
            <td className="px-6 py-4"><MatchBadge status={r.match_status} reason={r.match_reason} /></td>
            <td className="px-6 py-4 text-muted-foreground">
              {r.velvet_name ? <>{r.velvet_name} {r.velvet_sku && <span className="text-xs">({r.velvet_sku})</span>}</> : '—'}
            </td>
            <td className="px-6 py-4 text-right">
              {canTrigger && (
                <Button variant="outline" size="sm" className="rounded-sm"
                        onClick={() => onLink(r)} data-testid={`link-product-${r.id}`}>
                  <Link2 className="h-3.5 w-3.5 mr-1" /> Vincular
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OrdersTable({ rows, canTrigger, onLink, onConvert }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b-2 border-foreground/15 text-left">
          <th className="px-6 py-3 velvet-eyebrow">Pedido</th>
          <th className="px-6 py-3 velvet-eyebrow">Cliente</th>
          <th className="px-6 py-3 velvet-eyebrow">Pago</th>
          <th className="px-6 py-3 velvet-eyebrow">Envío</th>
          <th className="px-6 py-3 velvet-eyebrow">Total</th>
          <th className="px-6 py-3 velvet-eyebrow">Fecha</th>
          <th className="px-6 py-3 velvet-eyebrow"></th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={7} className="px-6 py-16 text-center text-muted-foreground">Sin pedidos importados.</td></tr>
        )}
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-border" data-testid={`sq-order-${r.id}`}>
            <td className="px-6 py-4">
              <div className="font-medium flex items-center gap-2">
                {r.order_number || '—'}
                {r.is_simulated && <span className="text-[9px] uppercase tracking-[0.14em] px-1 py-0.5 border border-[hsl(38_60%_60%)] bg-[hsl(48_100%_92%)]">Sim</span>}
                {r.test_mode && <span className="text-[9px] uppercase tracking-[0.14em] px-1 py-0.5 border border-border text-muted-foreground">Test</span>}
                {r.velvet_order_number && (
                  <span className="text-[9px] uppercase tracking-[0.14em] px-1 py-0.5 border border-[hsl(140_30%_60%)] bg-[hsl(140_40%_92%)]" data-testid={`converted-badge-${r.id}`}>
                    {r.velvet_order_number}
                  </span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">{r.external_id}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {(r.line_items?.length ?? 0)} artículo(s)
              </div>
            </td>
            <td className="px-6 py-4">
              <div>{r.velvet_customer_name || `${r.customer_first_name || ''} ${r.customer_last_name || ''}`.trim() || '—'}</div>
              <div className="text-xs text-muted-foreground">{r.customer_email || '—'}</div>
              <div className="mt-1"><MatchBadge status={r.customer_match_status} reason={r.customer_match_reason} tiny /></div>
            </td>
            <td className="px-6 py-4">
              <span className={`text-xs font-medium uppercase tracking-[0.14em] ${PAY_COLOR[r.payment_state] || 'text-muted-foreground'}`}
                    data-testid={`pay-state-${r.id}`}>
                {r.payment_state || '—'}
              </span>
            </td>
            <td className="px-6 py-4">
              <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{r.fulfillment_status || '—'}</span>
            </td>
            <td className="px-6 py-4 velvet-money">{r.grand_total ? `${r.currency || '$'} ${Number(r.grand_total).toFixed(2)}` : '—'}</td>
            <td className="px-6 py-4 text-muted-foreground text-xs">{fmtDate(r.modified_on || r.created_on)}</td>
            <td className="px-6 py-4 text-right">
              {canTrigger && (
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" className="rounded-sm"
                          onClick={() => onLink(r)} data-testid={`link-order-${r.id}`}>
                    <Link2 className="h-3.5 w-3.5 mr-1" /> Cliente
                  </Button>
                  {r.velvet_order_id ? (
                    <Button size="sm" className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                            onClick={() => onConvert(r, /*openExisting=*/true)}
                            data-testid={`open-velvet-order-${r.id}`}>
                      Abrir {r.velvet_order_number}
                    </Button>
                  ) : (
                    <Button size="sm"
                            className="rounded-sm bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40"
                            disabled={r.is_simulated}
                            onClick={() => onConvert(r, /*openExisting=*/false)}
                            data-testid={`convert-order-${r.id}`}
                            title={r.is_simulated ? 'Los registros simulados no pueden convertirse' : ''}>
                      Convertir en pedido Velvet
                    </Button>
                  )}
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MatchBadge({ status, reason, tiny }) {
  const s = MATCH_LABEL[status] || MATCH_LABEL.pending;
  const size = tiny ? 'text-[9px] px-1 py-0.5' : 'text-[10px] px-2 py-0.5';
  return (
    <span title={reason || ''}
          className={`inline-block ${size} uppercase tracking-[0.14em] border ${s.cls}`}
          data-testid={`match-badge-${status}`}>
      {s.label}
    </span>
  );
}

function LinkDialog({ title, description, options, currentId, onSubmit, onClose }) {
  const [id, setId] = useState(currentId || '');
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-sm" data-testid="sq-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="pt-2">
          <Label>Registro Velvet</Label>
          <Select value={id} onValueChange={setId}>
            <SelectTrigger className="rounded-sm" data-testid="link-target-select"><SelectValue placeholder="Elegir…" /></SelectTrigger>
            <SelectContent>
              {options.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter className="flex justify-between">
          <Button variant="ghost" className="rounded-sm text-destructive"
                  onClick={() => onSubmit({ unlink: true })}
                  data-testid="sq-unlink-btn">Desvincular</Button>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancelar</Button>
            <Button onClick={() => onSubmit(title.includes('cliente') ? { customer_id: id } : { product_id: id })}
                    disabled={!id}
                    className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                    data-testid="sq-link-confirm-btn">
              Vincular
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
